// pages/Agents/AgentSpace/AgentSpaceAgentSettings.js
// Dedicated configuration screen for a single agent.
// Shown on the main canvas when the user clicks the gear icon next to an
// agent in the FAB list â€” AgentSpace.js dispatches here based on
// AgentSpaceContext.agentMode === 'settings'.
//
// Sections (top to bottom):
//   - Header (back arrow + agent icon + name + capability summary)
//   - Connection card (only for connection-based agents):
//       OAuth status, connect/disconnect, connected-email display
//   - Prompt card (custom system prompt â€” visible for every agent):
//       textarea, Save / Reset to default
//   - "Open chat" CTA â€” returns to the chat with this agent
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Skeleton,
    Tooltip,
    Chip,
    Button,
    TextField,
    Snackbar,
    Alert,
    CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BoltIcon from '@mui/icons-material/Bolt';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import axiosInstance from '../../../../utils/axiosInstance';
import { useAgentSpace } from '../../../../contexts/AgentSpaceContext';
import AgentSpaceGeneralSourceCard from './AgentSpaceGeneralSourceCard';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
    success: '#059669',
    danger: '#dc2626',
    warning: '#f59e0b',
};

const ICON_MAP = {
    PollIcon: (s) => <PollIcon sx={{ fontSize: s }} />,
    PeopleIcon: (s) => <PeopleIcon sx={{ fontSize: s }} />,
    ChatIcon: (s) => <ChatIcon sx={{ fontSize: s }} />,
    EventIcon: (s) => <EventIcon sx={{ fontSize: s }} />,
    MailOutlineIcon: (s) => <MailOutlineIcon sx={{ fontSize: s }} />,
    EmailIcon: (s) => <EmailIcon sx={{ fontSize: s }} />,
    CalendarMonthIcon: (s) => <CalendarMonthIcon sx={{ fontSize: s }} />,
    CleaningServicesIcon: (s) => <CleaningServicesIcon sx={{ fontSize: s }} />,
};

function getIcon(name, size = 22) {
    const fn = ICON_MAP[name];
    return fn ? fn(size) : <AutoAwesomeIcon sx={{ fontSize: size }} />;
}

export default function AgentSpaceAgentSettings({ chatbotId, agentType, onBack }) {
    const { selectAgent } = useAgentSpace();

    // ---- Agent definition + connection status ----
    const [agentDef, setAgentDef] = useState(null);
    const [loadingDef, setLoadingDef] = useState(true);
    const [connectionStatus, setConnectionStatus] = useState(null);

    // ---- Capabilities (what the agent can do + example prompts) ----
    const [capabilities, setCapabilities] = useState([]);
    const [examplePrompts, setExamplePrompts] = useState([]);
    const [loadingCapabilities, setLoadingCapabilities] = useState(true);

    // ---- Prompt state ----
    const [prompt, setPrompt] = useState('');
    const [defaultPrompt, setDefaultPrompt] = useState('');
    const [isDefaultPrompt, setIsDefaultPrompt] = useState(true);
    const [loadingPrompt, setLoadingPrompt] = useState(true);
    const [savingPrompt, setSavingPrompt] = useState(false);

    // ---- Connection actions ----
    const [connectingAgent, setConnectingAgent] = useState(false);
    const [disconnectingAgent, setDisconnectingAgent] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Resolve which connection key to use (gmail_cleanup reuses 'gmail').
    const connectionKey = agentDef?.connection_type || agentType;

    // ---- Fetch def + connection ----
    const reloadConnection = useCallback(() => {
        if (!chatbotId) return;
        axiosInstance.get(`/api/agent-space/connections?chatbot_id=${chatbotId}`)
            .then((res) => {
                if (!res.data?.success) return;
                const all = res.data.data || {};
                setConnectionStatus(all[connectionKey] || null);
            })
            .catch(() => { /* ignore */ });
    }, [chatbotId, connectionKey]);

    useEffect(() => {
        if (!chatbotId || !agentType) return;
        let cancelled = false;
        setLoadingDef(true);
        Promise.all([
            axiosInstance.get('/api/agent-space/agent-types'),
            axiosInstance.get(`/api/agent-space/connections?chatbot_id=${chatbotId}`),
        ])
            .then(([typesRes, connRes]) => {
                if (cancelled) return;
                const def = (typesRes.data?.data || []).find((a) => a.id === agentType);
                setAgentDef(def || null);
                const connKey = def?.connection_type || agentType;
                const statuses = connRes.data?.data || {};
                setConnectionStatus(statuses[connKey] || null);
            })
            .catch(() => { /* ignore */ })
            .finally(() => { if (!cancelled) setLoadingDef(false); });
        return () => { cancelled = true; };
    }, [chatbotId, agentType]);

    // ---- Fetch capabilities ----
    useEffect(() => {
        if (!agentType) return;
        let cancelled = false;
        setLoadingCapabilities(true);
        axiosInstance.get(`/api/agent-space/agent-types/${agentType}/capabilities`)
            .then((res) => {
                if (cancelled || !res.data?.success) return;
                const data = res.data.data || {};
                setCapabilities(data.capabilities || []);
                setExamplePrompts(data.example_prompts || []);
            })
            .catch(() => { /* ignore */ })
            .finally(() => { if (!cancelled) setLoadingCapabilities(false); });
        return () => { cancelled = true; };
    }, [agentType]);

    // ---- Fetch prompt ----
    useEffect(() => {
        if (!chatbotId || !agentType) return;
        let cancelled = false;
        setLoadingPrompt(true);
        axiosInstance.get(`/api/agent-space/prompts/${agentType}?chatbot_id=${chatbotId}`)
            .then((res) => {
                if (cancelled || !res.data?.success) return;
                const data = res.data.data || {};
                setPrompt(data.prompt || data.default_prompt || '');
                setDefaultPrompt(data.default_prompt || '');
                setIsDefaultPrompt(!!data.is_default);
            })
            .catch(() => { /* ignore */ })
            .finally(() => { if (!cancelled) setLoadingPrompt(false); });
        return () => { cancelled = true; };
    }, [chatbotId, agentType]);

    // ---- Connection actions ----
    const handleConnect = async () => {
        if (!chatbotId) return;
        setConnectingAgent(true);
        try {
            const resp = await axiosInstance.get(
                `/api/agent-space/connections/${agentType}/auth/start?chatbot_id=${chatbotId}`
            );
            if (resp.data?.success && resp.data.data?.auth_url) {
                // Bounce to the provider OAuth flow. They'll redirect back.
                window.location.href = resp.data.data.auth_url;
            } else {
                setSnackbar({ open: true, message: resp.data?.message || 'Could not start the connection.', severity: 'error' });
            }
        } catch (e) {
            setSnackbar({ open: true, message: 'Could not start the connection.', severity: 'error' });
        } finally {
            setConnectingAgent(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Disconnect this account? You will need to reconnect to use the agent.')) return;
        setDisconnectingAgent(true);
        try {
            const resp = await axiosInstance.post(
                `/api/agent-space/connections/${agentType}/disconnect?chatbot_id=${chatbotId}`
            );
            if (resp.data?.success) {
                setSnackbar({ open: true, message: 'Account disconnected.', severity: 'success' });
                reloadConnection();
            } else {
                setSnackbar({ open: true, message: resp.data?.message || 'Disconnect failed.', severity: 'error' });
            }
        } catch (e) {
            setSnackbar({ open: true, message: 'Disconnect failed.', severity: 'error' });
        } finally {
            setDisconnectingAgent(false);
        }
    };

    // ---- Prompt actions ----
    const handleSavePrompt = async () => {
        if (!chatbotId) return;
        setSavingPrompt(true);
        try {
            const resp = await axiosInstance.post(
                `/api/agent-space/prompts/${agentType}?chatbot_id=${chatbotId}`,
                { prompt },
            );
            if (resp.data?.success) {
                setIsDefaultPrompt(false);
                setSnackbar({ open: true, message: 'Prompt saved.', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: resp.data?.message || 'Could not save the prompt.', severity: 'error' });
            }
        } catch (e) {
            setSnackbar({ open: true, message: 'Could not save the prompt.', severity: 'error' });
        } finally {
            setSavingPrompt(false);
        }
    };

    const handleResetPrompt = async () => {
        if (!window.confirm('Reset the prompt back to the default?')) return;
        setSavingPrompt(true);
        try {
            const resp = await axiosInstance.delete(
                `/api/agent-space/prompts/${agentType}?chatbot_id=${chatbotId}`
            );
            if (resp.data?.success) {
                setPrompt(defaultPrompt || '');
                setIsDefaultPrompt(true);
                setSnackbar({ open: true, message: 'Prompt reset.', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: resp.data?.message || 'Could not reset.', severity: 'error' });
            }
        } catch (e) {
            setSnackbar({ open: true, message: 'Could not reset.', severity: 'error' });
        } finally {
            setSavingPrompt(false);
        }
    };

    const agentColor = agentDef?.color || colors.accent;
    const requiresConnection = !!agentDef?.requires_connection;
    const isConnected = requiresConnection ? !!connectionStatus?.is_connected : true;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', fontFamily }}>
            {/* ---- Header bar ---- */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: { xs: 1.5, sm: 2.5 }, py: 1.25,
                bgcolor: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '14px', mb: 1.5, flexShrink: 0,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Tooltip title="Back to agents">
                        <IconButton size="small" onClick={onBack} sx={{ color: colors.textSecondary }}>
                            <ArrowBackIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: '11px',
                        bgcolor: `${agentColor}14`, color: agentColor,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        {getIcon(agentDef?.icon_name, 20)}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily, fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: 700, color: colors.textPrimary }} noWrap>
                            {agentDef?.label || agentType} settings
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary }} noWrap>
                            Configure connection and prompt for this agent.
                        </Typography>
                    </Box>
                </Box>
                <Button
                    size="small"
                    startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => selectAgent(agentType)}
                    sx={{
                        fontFamily, fontSize: '0.72rem', fontWeight: 600,
                        textTransform: 'none', color: '#fff',
                        background: `linear-gradient(135deg, ${agentColor}, ${agentColor}cc)`,
                        borderRadius: '10px', px: 1.5, py: 0.75,
                        boxShadow: `0 4px 12px ${agentColor}33`,
                        '&:hover': { background: agentColor, opacity: 0.92 },
                    }}
                >
                    Open chat
                </Button>
            </Box>

            {/* ---- Body ---- */}
            <Box sx={{ flex: 1, overflow: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Agent summary card */}
                {loadingDef ? (
                    <Skeleton variant="rounded" height={104} sx={{ borderRadius: '14px' }} />
                ) : (
                    <Box sx={{
                        p: 2, borderRadius: '14px',
                        bgcolor: `${agentColor}08`,
                        border: `1px solid ${agentColor}24`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 700, color: colors.textPrimary }}>
                                About this agent
                            </Typography>
                            <Chip
                                size="small"
                                icon={isConnected
                                    ? <CheckCircleOutlineIcon sx={{ fontSize: '14px !important', color: `${colors.success} !important` }} />
                                    : <ErrorOutlineIcon sx={{ fontSize: '14px !important', color: '#f59e0b !important' }} />}
                                label={isConnected ? (connectionStatus?.connected_email ? 'Connected' : 'Ready') : 'Not configured'}
                                sx={{
                                    fontFamily, fontSize: '0.6rem', fontWeight: 700, height: 22,
                                    color: isConnected ? colors.success : '#f59e0b',
                                    bgcolor: isConnected ? 'rgba(5,150,105,0.08)' : 'rgba(245,158,11,0.08)',
                                    border: `1px solid ${isConnected ? 'rgba(5,150,105,0.18)' : 'rgba(245,158,11,0.18)'}`,
                                    '& .MuiChip-icon': { ml: '4px' },
                                }}
                            />
                        </Box>
                        <Typography sx={{ fontFamily, fontSize: '0.78rem', color: colors.textSecondary, lineHeight: 1.55 }}>
                            {agentDef?.capability_description || agentDef?.description || 'No description available.'}
                        </Typography>
                    </Box>
                )}

                {/* ---- Source card (general agent only) ---- */}
                {agentType === 'general' && (
                    <AgentSpaceGeneralSourceCard
                        chatbotId={chatbotId}
                        onSnack={setSnackbar}
                    />
                )}

                {/* ---- Connection card (connection-based agents only) ---- */}
                {requiresConnection && (
                    <Box sx={{
                        p: 2, borderRadius: '14px',
                        bgcolor: 'rgba(255,255,255,0.85)',
                        border: '1px solid rgba(0,0,0,0.06)',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.88rem', fontWeight: 700, color: colors.textPrimary }}>
                                    Connection
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary, mt: 0.25 }}>
                                    Link the account this agent will use.
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            {isConnected ? (
                                <>
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.75,
                                        px: 1.25, py: 0.75, borderRadius: '10px',
                                        bgcolor: 'rgba(5,150,105,0.08)',
                                        border: '1px solid rgba(5,150,105,0.2)',
                                    }}>
                                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: colors.success }} />
                                        <Typography sx={{ fontFamily, fontSize: '0.74rem', fontWeight: 600, color: colors.success }}>
                                            {connectionStatus?.connected_email || 'Connected'}
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        startIcon={<LinkOffIcon sx={{ fontSize: '16px !important' }} />}
                                        onClick={handleDisconnect}
                                        disabled={disconnectingAgent}
                                        sx={{
                                            fontFamily, fontSize: '0.72rem', fontWeight: 600,
                                            textTransform: 'none', color: colors.danger,
                                            border: `1px solid ${colors.danger}40`,
                                            borderRadius: '10px', px: 1.5, py: 0.65,
                                            '&:hover': { bgcolor: `${colors.danger}10` },
                                        }}
                                    >
                                        {disconnectingAgent ? 'Disconnectingâ€¦' : 'Disconnect'}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    size="small"
                                    startIcon={<LinkIcon sx={{ fontSize: '16px !important' }} />}
                                    onClick={handleConnect}
                                    disabled={connectingAgent}
                                    sx={{
                                        fontFamily, fontSize: '0.78rem', fontWeight: 600,
                                        textTransform: 'none', color: '#fff',
                                        background: `linear-gradient(135deg, ${agentColor}, ${agentColor}cc)`,
                                        borderRadius: '10px', px: 2, py: 0.85,
                                        '&:hover': { background: agentColor, opacity: 0.92 },
                                    }}
                                >
                                    {connectingAgent ? 'Opening providerâ€¦' : `Connect ${agentDef?.label || agentType}`}
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}

                {/* The generic "What you can ask" + "Agent prompt" cards are
                    replaced by the dedicated 3-step wizard for the General
                    agent, so hide them there. */}
                {agentType !== 'general' && (<>
                {/* ---- What you can ask card ---- */}
                <Box sx={{
                    p: 2, borderRadius: '14px',
                    bgcolor: 'rgba(255,255,255,0.85)',
                    border: '1px solid rgba(0,0,0,0.06)',
                }}>
                    <Typography sx={{ fontFamily, fontSize: '0.88rem', fontWeight: 700, color: colors.textPrimary }}>
                        What you can ask
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary, mt: 0.25, mb: 1.5 }}>
                        Phrase your message in plain English. This agent can perform the following actions for you.
                    </Typography>

                    {loadingCapabilities ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={36} sx={{ borderRadius: '8px' }} />)}
                        </Box>
                    ) : (
                        <>
                            {/* Example prompts â€” concrete sentences the user can copy-paste. */}
                            {examplePrompts.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{
                                        fontFamily, fontSize: '0.62rem', fontWeight: 700,
                                        color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75,
                                    }}>
                                        Try saying
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                        {examplePrompts.map((q, i) => (
                                            <Box key={i} sx={{
                                                display: 'flex', alignItems: 'flex-start', gap: 0.75,
                                                px: 1.25, py: 0.75, borderRadius: '10px',
                                                bgcolor: `${agentColor}08`,
                                                border: `1px solid ${agentColor}1a`,
                                            }}>
                                                <FormatQuoteIcon sx={{ fontSize: 13, color: agentColor, mt: '2px', flexShrink: 0, transform: 'scaleX(-1)' }} />
                                                <Typography sx={{
                                                    fontFamily, fontSize: '0.74rem',
                                                    color: colors.textPrimary, lineHeight: 1.5, flex: 1,
                                                }}>
                                                    {q}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Action capability list */}
                            {capabilities.length > 0 && (
                                <Box>
                                    <Typography sx={{
                                        fontFamily, fontSize: '0.62rem', fontWeight: 700,
                                        color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75,
                                    }}>
                                        Actions this agent can perform
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {capabilities.map((cap) => (
                                            <Box
                                                key={cap.action_type}
                                                sx={{
                                                    display: 'flex', alignItems: 'flex-start', gap: 0.85,
                                                    px: 1, py: 0.6, borderRadius: '8px',
                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.025)' },
                                                }}
                                            >
                                                <Box sx={{
                                                    mt: '3px',
                                                    width: 18, height: 18, borderRadius: '6px',
                                                    bgcolor: `${agentColor}14`, color: agentColor,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <BoltIcon sx={{ fontSize: 12 }} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.74rem', fontWeight: 600, color: colors.textPrimary }}>
                                                        {cap.label}
                                                    </Typography>
                                                    {cap.description && (
                                                        <Typography sx={{
                                                            fontFamily, fontSize: '0.68rem', color: colors.textSecondary,
                                                            lineHeight: 1.45, mt: 0.1,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                        }}>
                                                            {cap.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {capabilities.length === 0 && examplePrompts.length === 0 && (
                                <Typography sx={{ fontFamily, fontSize: '0.74rem', color: colors.textTertiary }}>
                                    Capabilities are not available for this agent yet.
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                {/* ---- Prompt card ---- */}
                <Box sx={{
                    p: 2, borderRadius: '14px',
                    bgcolor: 'rgba(255,255,255,0.85)',
                    border: '1px solid rgba(0,0,0,0.06)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25, flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.88rem', fontWeight: 700, color: colors.textPrimary }}>
                                Agent prompt
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary, mt: 0.25 }}>
                                Customize how this agent behaves. The default works well â€” edit only when you need agent-specific guidance.
                            </Typography>
                        </Box>
                        <Chip
                            size="small"
                            label={isDefaultPrompt ? 'Using default' : 'Custom'}
                            sx={{
                                fontFamily, fontSize: '0.6rem', fontWeight: 700, height: 22,
                                color: isDefaultPrompt ? colors.textTertiary : agentColor,
                                bgcolor: isDefaultPrompt ? 'rgba(0,0,0,0.04)' : `${agentColor}14`,
                                border: `1px solid ${isDefaultPrompt ? 'rgba(0,0,0,0.08)' : `${agentColor}30`}`,
                            }}
                        />
                    </Box>

                    {loadingPrompt ? (
                        <Skeleton variant="rounded" height={160} sx={{ borderRadius: '10px' }} />
                    ) : (
                        <TextField
                            multiline
                            fullWidth
                            minRows={6}
                            maxRows={20}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="Enter the prompt that will guide this agent..."
                            disabled={savingPrompt}
                            InputProps={{
                                sx: {
                                    fontFamily: "'Fira Code', monospace",
                                    fontSize: '0.78rem',
                                    lineHeight: 1.55,
                                    borderRadius: '10px',
                                    bgcolor: '#fff',
                                },
                            }}
                        />
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.25 }}>
                        <Button
                            size="small"
                            startIcon={<RestartAltIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={handleResetPrompt}
                            disabled={savingPrompt || (isDefaultPrompt && prompt === defaultPrompt)}
                            sx={{
                                fontFamily, fontSize: '0.72rem', fontWeight: 600,
                                textTransform: 'none', color: colors.textSecondary,
                                border: '1px solid rgba(0,0,0,0.1)', borderRadius: '10px',
                                px: 1.5, py: 0.65,
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                            }}
                        >
                            Reset to default
                        </Button>
                        <Button
                            size="small"
                            startIcon={
                                savingPrompt
                                    ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                    : <SaveOutlinedIcon sx={{ fontSize: '16px !important' }} />
                            }
                            onClick={handleSavePrompt}
                            disabled={savingPrompt || loadingPrompt}
                            sx={{
                                fontFamily, fontSize: '0.72rem', fontWeight: 600,
                                textTransform: 'none', color: '#fff',
                                background: `linear-gradient(135deg, ${agentColor}, ${agentColor}cc)`,
                                borderRadius: '10px', px: 2, py: 0.65,
                                boxShadow: `0 4px 12px ${agentColor}33`,
                                '&:hover': { background: agentColor, opacity: 0.92 },
                            }}
                        >
                            {savingPrompt ? 'Savingâ€¦' : 'Save prompt'}
                        </Button>
                    </Box>
                </Box>
                </>)}
            </Box>

            <Snackbar
                open={snackbar.open}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                autoHideDuration={3500}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ fontFamily, fontSize: '0.78rem' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
